import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserProvider, Contract, formatEther, formatUnits } from 'ethers';
import onboard from '../config/onboard';
import {
  LOB_TOKEN_ABI,
  LOB_TOKEN_ADDRESS,
  BASE_CHAIN_PARAMS,
  USDC_TOKEN_ABI,
  USDC_TOKEN_ADDRESS,
  WORKLOOB_NET,
} from '../config/Constants';

const Web3Context = createContext();

const EXPECTED_CHAIN_ID = BASE_CHAIN_PARAMS.chainId;

async function readWalletChainId(walletProvider) {
  const hex = await walletProvider.request({ method: 'eth_chainId' });
  return parseInt(hex, 16);
}

async function ensureWorkloobNetwork(walletProvider) {
  const chainIdHex = `0x${EXPECTED_CHAIN_ID.toString(16)}`;

  let currentChainId = await readWalletChainId(walletProvider);
  if (currentChainId === EXPECTED_CHAIN_ID) {
    return true;
  }

  console.warn(
    `Wallet on chain ${currentChainId}, expected ${EXPECTED_CHAIN_ID} (${WORKLOOB_NET.network}). Prompting switch…`
  );

  try {
    await walletProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: chainIdHex }],
    });
  } catch (switchError) {
    if (switchError?.code === 4902) {
      await walletProvider.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: chainIdHex,
          chainName: BASE_CHAIN_PARAMS.chainName,
          nativeCurrency: BASE_CHAIN_PARAMS.nativeCurrency,
          rpcUrls: BASE_CHAIN_PARAMS.rpcUrls,
          blockExplorerUrls: BASE_CHAIN_PARAMS.blockExplorerUrls,
        }],
      });
    } else if (switchError?.code === 4001) {
      console.warn('User rejected network switch');
      return false;
    } else {
      throw switchError;
    }
  }

  currentChainId = await readWalletChainId(walletProvider);
  if (currentChainId !== EXPECTED_CHAIN_ID) {
    console.warn(`Still on chain ${currentChainId} after switch attempt`);
    return false;
  }

  console.log(`Wallet switched to ${BASE_CHAIN_PARAMS.chainName} (${EXPECTED_CHAIN_ID})`);
  return true;
}

async function fetchAllBalances(ethProvider, addr) {
  let ethBal = '0';
  let usdcBal = '0';
  let lobBal = '0';

  try {
    const balance = await ethProvider.getBalance(addr);
    ethBal = formatEther(balance);
  } catch (err) {
    console.error('ETH (gas) balance fetch error:', err);
  }

  try {
    const network = await ethProvider.getNetwork();
    if (Number(network.chainId) !== EXPECTED_CHAIN_ID) {
      console.warn('Skipping USDC fetch — provider chain mismatch:', Number(network.chainId));
      return { ethBal, usdcBal: '0', lobBal: '0', onCorrectNetwork: false };
    }

    const usdc = new Contract(USDC_TOKEN_ADDRESS, USDC_TOKEN_ABI, ethProvider);
    const [decimals, bal] = await Promise.all([
      usdc.decimals(),
      usdc.balanceOf(addr),
    ]);
    usdcBal = formatUnits(bal, Number(decimals));
    console.log(
      'USDC balance:',
      usdcBal,
      '| token:',
      USDC_TOKEN_ADDRESS,
      '| chain:',
      Number(network.chainId),
      '| wallet:',
      addr
    );
  } catch (err) {
    console.error(
      'USDC balance fetch failed:',
      err?.message || err,
      '| token:',
      USDC_TOKEN_ADDRESS,
      '| wallet:',
      addr
    );
  }

  try {
    const contract = new Contract(LOB_TOKEN_ADDRESS, LOB_TOKEN_ABI, ethProvider);
    const tokenBal = await contract.balanceOf(addr);
    lobBal = formatEther(tokenBal);
  } catch (err) {
    console.log('LOB token balance fetch failed (non-fatal):', err?.message || err);
  }

  return { ethBal, usdcBal, lobBal, onCorrectNetwork: true };
}

export const Web3Provider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(() => {
    return localStorage.getItem('walletConnected') === 'true';
  });
  const [walletAddress, setWalletAddress] = useState(() => {
    return localStorage.getItem('walletAddress') || null;
  });
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [walletBalance, setWalletBalance] = useState('0');
  const [ethBalance, setEthBalance] = useState('0');
  const [lobTokenBalance, setLobTokenBalance] = useState('0');
  const [onCorrectNetwork, setOnCorrectNetwork] = useState(false);
  const rawWalletProviderRef = useRef(null);
  const chainListenerRef = useRef(null);

  const shortenAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const applyBalances = useCallback(({ ethBal, usdcBal, lobBal, onCorrectNetwork: ok }) => {
    setEthBalance(ethBal);
    setWalletBalance(ok ? usdcBal : '0');
    setLobTokenBalance(lobBal);
    setOnCorrectNetwork(!!ok);
  }, []);

  const syncFromWallet = useCallback(async (w, { promptSwitch = true } = {}) => {
    if (!w?.provider) return null;

    const addr = w.accounts?.[0]?.address || null;
    rawWalletProviderRef.current = w.provider;

    if (promptSwitch) {
      const switched = await ensureWorkloobNetwork(w.provider);
      if (!switched) {
        setOnCorrectNetwork(false);
        setWalletBalance('0');
      }
    }

    const ethProvider = new BrowserProvider(w.provider);
    const ethSigner = await ethProvider.getSigner();

    setProvider(ethProvider);
    setSigner(ethSigner);
    setWalletAddress(addr);
    setIsConnected(!!addr);

    if (addr) {
      localStorage.setItem('walletConnected', 'true');
      localStorage.setItem('walletAddress', addr);
      if (w.label) {
        localStorage.setItem('walletLabel', w.label);
      }

      const balances = await fetchAllBalances(ethProvider, addr);
      applyBalances(balances);
    }

    return { wallet: w, address: addr, provider: ethProvider };
  }, [applyBalances]);

  const connectWallet = async () => {
    try {
      if (!onboard || typeof onboard.connectWallet !== 'function') {
        console.error('onboard not initialized');
        return null;
      }
      const connected = await onboard.connectWallet();
      if (!connected || connected.length === 0) return null;
      return syncFromWallet(connected[0], { promptSwitch: true });
    } catch (err) {
      console.error('connectWallet error', err?.message || err);
      return null;
    }
  };

  const disconnectWallet = async () => {
    try {
      if (chainListenerRef.current && rawWalletProviderRef.current?.removeListener) {
        rawWalletProviderRef.current.removeListener('chainChanged', chainListenerRef.current);
      }
      chainListenerRef.current = null;
      rawWalletProviderRef.current = null;

      const wallets = onboard.state.get().wallets;
      if (wallets && wallets.length > 0) {
        await onboard.disconnectWallet({ label: wallets[0].label });
      }
    } catch (err) {
      console.error('disconnect error', err);
    }
    setIsConnected(false);
    setWalletAddress(null);
    setProvider(null);
    setSigner(null);
    setWalletBalance('0');
    setLobTokenBalance('0');
    setOnCorrectNetwork(false);

    localStorage.removeItem('walletConnected');
    localStorage.removeItem('walletAddress');
    localStorage.removeItem('walletLabel');
  };

  // On mount: restore wallet, subscribe to changes, listen for chain switches
  useEffect(() => {
    const handleChainChanged = async () => {
      const wallets = onboard.state.get().wallets;
      if (!wallets?.length) return;
      console.log('chainChanged — re-syncing wallet and balances');
      await syncFromWallet(wallets[0], { promptSwitch: true });
    };

    const attachChainListener = (walletProvider) => {
      if (!walletProvider?.on) return;
      if (chainListenerRef.current && rawWalletProviderRef.current?.removeListener) {
        rawWalletProviderRef.current.removeListener('chainChanged', chainListenerRef.current);
      }
      chainListenerRef.current = () => {
        handleChainChanged();
      };
      rawWalletProviderRef.current = walletProvider;
      walletProvider.on('chainChanged', chainListenerRef.current);
    };

    const init = async () => {
      try {
        const wasConnected = localStorage.getItem('walletConnected') === 'true';
        const savedAddress = localStorage.getItem('walletAddress');
        const savedWalletLabel = localStorage.getItem('walletLabel');

        const wallets = onboard.state.get().wallets;
        if (wallets && wallets.length > 0) {
          attachChainListener(wallets[0].provider);
          await syncFromWallet(wallets[0], { promptSwitch: true });
        } else if (wasConnected && savedAddress) {
          console.log('Wallet was previously connected, attempting to auto-reconnect…');
          try {
            const connected = await onboard.connectWallet({
              autoSelect: {
                label: savedWalletLabel || 'MetaMask',
                disableModals: true,
              },
            });
            if (connected?.length) {
              attachChainListener(connected[0].provider);
              await syncFromWallet(connected[0], { promptSwitch: true });
            }
          } catch (autoConnectError) {
            console.warn('Auto-reconnect failed:', autoConnectError);
          }
        }
      } catch (err) {
        console.error('Web3 init error', err);
        localStorage.removeItem('walletConnected');
        localStorage.removeItem('walletAddress');
        localStorage.removeItem('walletLabel');
      }
    };

    let unsub = { unsubscribe: () => {} };
    try {
      if (onboard?.state?.select) {
        unsub = onboard.state.select('wallets').subscribe(async (wallets) => {
          if (!wallets || wallets.length === 0) {
            if (chainListenerRef.current && rawWalletProviderRef.current?.removeListener) {
              rawWalletProviderRef.current.removeListener('chainChanged', chainListenerRef.current);
            }
            chainListenerRef.current = null;
            rawWalletProviderRef.current = null;

            setIsConnected(false);
            setWalletAddress(null);
            setProvider(null);
            setSigner(null);
            setWalletBalance('0');
            setEthBalance('0');
            setLobTokenBalance('0');
            setOnCorrectNetwork(false);
            localStorage.removeItem('walletConnected');
            localStorage.removeItem('walletAddress');
            localStorage.removeItem('walletLabel');
            return;
          }

          attachChainListener(wallets[0].provider);
          await syncFromWallet(wallets[0], { promptSwitch: true });
        });
      }
    } catch (err) {
      console.error('onboard subscribe error', err?.message || err);
      unsub = { unsubscribe: () => {} };
    }

    init();

    return () => {
      unsub.unsubscribe();
      if (chainListenerRef.current && rawWalletProviderRef.current?.removeListener) {
        rawWalletProviderRef.current.removeListener('chainChanged', chainListenerRef.current);
      }
    };
  }, [syncFromWallet]);

  // Refresh balances on an interval when connected on the correct network
  useEffect(() => {
    if (!walletAddress || !provider || !isConnected) {
      setWalletBalance('0');
      setEthBalance('0');
      setLobTokenBalance('0');
      return undefined;
    }

    const refresh = async () => {
      if (rawWalletProviderRef.current) {
        const chainId = await readWalletChainId(rawWalletProviderRef.current);
        if (chainId !== EXPECTED_CHAIN_ID) {
          const switched = await ensureWorkloobNetwork(rawWalletProviderRef.current);
          if (!switched) {
            setOnCorrectNetwork(false);
            setWalletBalance('0');
            return;
          }
        }
      }

      const balances = await fetchAllBalances(provider, walletAddress);
      applyBalances(balances);
    };

    refresh();
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [walletAddress, provider, isConnected, applyBalances]);

  const value = {
    isConnected,
    walletAddress,
    provider,
    signer,
    walletBalance,
    ethBalance,
    lobTokenBalance,
    onCorrectNetwork,
    expectedChainId: EXPECTED_CHAIN_ID,
    networkName: BASE_CHAIN_PARAMS.chainName,
    shortenAddress,
    connectWallet,
    disconnectWallet,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (context === undefined) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};
